import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Stack,
    Typography,
} from '@mui/material';
import type { StoreDetailsNotification } from '../../../../types/features/storeDetails/notifications';
import AdminSurface from '../../../../components/admin/AdminSurface';
import AdminSearchField from '../../../../components/admin/AdminSearchField';
import AdminStatusPill from '../../../../components/admin/AdminStatusPill';
import { adminColors, adminTypography } from '../../../../styles/adminDesignTokens';
import {
    adminButtonSx,
    adminButtonToneSx,
    adminCombineSx,
    adminFormControlSx,
    adminFormFieldSx,
    adminTableSx,
} from '../../../../styles/adminComponentStyles';

type StoreDetailsNotificationsTabProps = {
    error?: string;
    loading?: boolean;
    notifications?: StoreDetailsNotification[];
    notificationStatuses?: string[];
    onRefresh?: () => void;
    onSendNotification?: () => void;
};

const StoreDetailsNotificationsTab: React.FC<StoreDetailsNotificationsTabProps> = ({
    error = '',
    loading = false,
    notifications = [],
    notificationStatuses = [],
    onRefresh,
    onSendNotification,
}) => {
    const [search, setSearch] = React.useState('');
    const [status, setStatus] = React.useState('all');
    const filteredNotifications = React.useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        return notifications.filter((notification) => {
            const matchesStatus = status === 'all' || notification.status === status;
            const searchableText = [
                notification.reportDate,
                notification.user,
                notification.title,
                notification.message,
                notification.status,
                notification.viewedAt,
            ].filter(Boolean).join(' ').toLowerCase();
            return matchesStatus && (!normalizedSearch || searchableText.includes(normalizedSearch));
        });
    }, [notifications, search, status]);

    return (
    <>
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 1.5, px: 2 }}
        >
            <AdminSearchField
                placeholder="Search..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={adminCombineSx(adminFormFieldSx, { width: { xs: '100%', sm: 240 } })}
                inputProps={{ 'aria-label': 'Search notifications' }}
            />
            <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ minWidth: 0 }}>
                <FormControl size="small" sx={adminCombineSx(adminFormControlSx, { minWidth: { sm: 160 } })}>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={status} onChange={(event) => setStatus(event.target.value)}>
                        <MenuItem value="all">All</MenuItem>
                        {notificationStatuses.map((notificationStatus) => (
                            <MenuItem key={notificationStatus} value={notificationStatus}>
                                {notificationStatus.charAt(0).toUpperCase() + notificationStatus.slice(1)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {onSendNotification ? (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={onSendNotification}
                        sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary)}
                    >
                        Send Notification
                    </Button>
                ) : null}
            </Stack>
        </Stack>
        {error ? (
            <AdminSurface sx={{ p: 2, mb: 1.5 }}>
                <Typography sx={{ ...adminTypography.body, color: adminColors.danger, mb: 1 }}>
                    {error}
                </Typography>
                {onRefresh ? (
                    <Button size="small" variant="outlined" onClick={onRefresh}>
                        Retry
                    </Button>
                ) : null}
            </AdminSurface>
        ) : null}

        {loading ? (
            <AdminSurface sx={{ border: 0, boxShadow: 'none', display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
            </AdminSurface>
        ) : (
            <TableContainer
                component={AdminSurface}
                sx={adminCombineSx(adminTableSx, { border: 'none', borderRadius: 0, mt: 1.5, overflowX: 'auto' })}
            >
                <Table sx={{
                    tableLayout: 'fixed',
                    minWidth: 980,
                    width: '100%',
                    '& .MuiTableCell-root': {
                        padding: '8px 12px !important',
                    },
                }}>
                    <TableHead sx={{ '& th': { padding: '8px 12px !important', textTransform: 'uppercase' } }}>
                        <TableRow>
                            <TableCell sx={{ width: 130 }}>Date</TableCell>
                            <TableCell sx={{ width: 180 }}>User</TableCell>
                            <TableCell sx={{ width: 220 }}>Title</TableCell>
                            <TableCell>Message</TableCell>
                            <TableCell sx={{ width: 130 }}>Status</TableCell>
                            <TableCell sx={{ width: 160 }}>Viewed At</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredNotifications.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText, py: 3, textAlign: 'center' }}>
                                        No notifications found for this store.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredNotifications.map((notification) => (
                                <TableRow key={notification.id}>
                                    <TableCell>
                                        <Typography sx={adminTypography.body}>
                                            {notification.reportDate || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={adminTypography.body}>
                                            {notification.user || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ ...adminTypography.bodyStrong, overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
                                            {notification.title || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ ...adminTypography.body, overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
                                            {notification.message || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <AdminStatusPill
                                            label={notification.status || 'Unknown'}
                                            tone={notification.statusTone}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={adminTypography.body}>
                                            {notification.viewedAt || '-'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        )}
    </>
    );
};

export default React.memo(StoreDetailsNotificationsTab);
